@file:NoLiveLiterals

package com.example.borshi.web.home

import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.model.Category
import com.example.borshi.web.model.NutritionistBasePlan
import com.example.borshi.web.model.NutritionistProfile
import com.example.borshi.web.model.Product

// ─────────────────────────────────────────────────────────────────────────────
// Repository Interfaces — define contracts; UI depends on abstractions, not
// concrete Firebase calls. Swap implementations in tests without touching UI.
// ─────────────────────────────────────────────────────────────────────────────

interface ProductRepository {
    suspend fun getCategories(): Result<List<Category>>
    suspend fun getProducts(categoryId: String? = null): Result<List<Product>>
    suspend fun getFeaturedProducts(limit: Int = AppConfig.FEATURED_ITEMS_LIMIT): Result<List<Product>>
}

interface NutritionRepository {
    suspend fun getNutritionists(): Result<List<NutritionistProfile>>
    suspend fun getPublicPlans(): Result<List<NutritionistBasePlan>>
}

// ─────────────────────────────────────────────────────────────────────────────
// Firebase Implementations
// ─────────────────────────────────────────────────────────────────────────────

class FirebaseProductRepository : ProductRepository {

    override suspend fun getCategories(): Result<List<Category>> = safeCall {
        val cats = FirebaseService.getCategories()
        if (cats.isEmpty()) getFallbackCategories() else cats
    }

    override suspend fun getProducts(categoryId: String?): Result<List<Product>> = safeCall {
        FirebaseService.getProducts()
    }

    override suspend fun getFeaturedProducts(limit: Int): Result<List<Product>> = safeCall {
        FirebaseService.getProducts().take(limit)
    }

    private fun getFallbackCategories(): List<Category> = listOf(
        Category("🐟", "Fish",         "Fresh catch",          FirebaseService.getStorageUrl("images/sea_fish.png"),        "fish"),
        Category("🦐", "Prawns",       "Jumbo & regular",      FirebaseService.getStorageUrl("images/shrimp.jpg"),          "prawns"),
        Category("🦞", "Shellfish",    "Crabs, lobsters",      FirebaseService.getStorageUrl("images/ic_shellfish.jpg"),    "shellfish"),
        Category("🍤", "Dried Seafood","Preserved delicacies", FirebaseService.getStorageUrl("images/ic_river_fish.jpg"),   "dried")
    )
}

class FirebaseNutritionRepository : NutritionRepository {

    override suspend fun getNutritionists(): Result<List<NutritionistProfile>> = safeCall {
        FirebaseService.getNutritionists()
    }

    override suspend fun getPublicPlans(): Result<List<NutritionistBasePlan>> = safeCall {
        FirebaseService.getPublicPlans()
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: wraps a suspending block into our domain Result<T>
// Named safeCall (not runCatching) to avoid shadowing kotlin.runCatching.
// ─────────────────────────────────────────────────────────────────────────────
private suspend fun <T> safeCall(block: suspend () -> T): Result<T> = try {
    Result.Success(block())
} catch (e: Throwable) {
    Result.Error(AppException.from(e))
}

